import { useEffect, useEffectEvent, useRef, useState, type ReactNode } from 'react';
import { loadMapbox, loadedMapbox, type Mapbox, type mapboxgl } from './mapbox_module.js';
import { ArrowsOutSimpleIcon, CrosshairIcon, MinusIcon, PlusIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import './results_map.css';

export type MapCoordinates = { latitude: number; longitude: number };

/** Só o que o Mapbox aceita: latitude fora de ±90 faz `LngLat.convert` LANÇAR
 *  ("Invalid LngLat latitude value") no `setLngLat` de um marcador — fora do
 *  `try` da inicialização, derrubaria a página em vez de cair no fallback.
 *  Longitude só precisa ser finita: fora de ±180 o Mapbox normaliza
 *  (`LngLat.wrap`). Devolve a própria coordenada quando serve, `undefined`
 *  quando não. */
export function validMapCoordinates(
  coordinates: MapCoordinates | undefined,
): MapCoordinates | undefined {
  if (!coordinates) return undefined;
  const { latitude, longitude } = coordinates;
  return Number.isFinite(latitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    Number.isFinite(longitude)
    ? coordinates
    : undefined;
}

/** Os controles de vista que o produto desenha por cima do mapa (no lugar do
 *  `NavigationControl` do Mapbox): + · − · localizar, e o expandir quando há
 *  um mapa cheio para onde ir. */
export type MapViewControlsCopy = {
  readonly zoomInLabel: string;
  readonly zoomOutLabel: string;
  readonly recenterLabel: string;
  readonly expandLabel?: string;
  readonly onExpand?: () => void;
};

/** Quanto o contador de resize espera antes de pedir reenquadramento. O
 *  `map.resize()` continua por quadro — o canvas precisa dele, senão a projeção
 *  fica com a largura velha. O que assenta é o ENQUADRAMENTO: numa divisória
 *  arrastável a mudança de tamanho chega uma vez por quadro, e reenquadrar em
 *  cada uma faz a câmera perseguir o cursor. 120ms é mais que um quadro e menos
 *  que a percepção de atraso ao soltar. */
const MAP_RESIZE_SETTLE_MS = 120;

type LiveMapRef = { readonly current: mapboxgl.Map | null };

/** One creation of the map, announced as state: a new object per instance, so an
 *  effect that depends on it runs again on every map rebuilt. */
export type MapboxInstance = { readonly map: mapboxgl.Map };

/**
 * The map an effect may touch: the one `instance` announced AND still alive in
 * `mapRef`. Between a removal and the next announcement the state still holds the
 * removed map, which any call would hit.
 */
export function liveMap(mapRef: LiveMapRef, instance: MapboxInstance | null): mapboxgl.Map | null {
  return instance !== null && mapRef.current === instance.map ? instance.map : null;
}

/**
 * Whether the map failed to come up, cleared when `resetKey` changes (a new token,
 * the map turned available again): the fallback is only for the attempt that
 * failed. Reset during render, so the retry starts in the same commit.
 */
export function useMapInitializationFailure(
  resetKey: string,
): readonly [boolean, (failed: boolean) => void] {
  const [failed, setFailed] = useState(false);
  const [failedKey, setFailedKey] = useState(resetKey);
  if (failedKey === resetKey) return [failed, setFailed];
  setFailedKey(resetKey);
  setFailed(false);
  return [false, setFailed];
}

/**
 * A instância do Mapbox — nasce quando há token, coordenada inicial e um
 * contêiner, e morre com eles. Comum a todo mapa de resultados (hotel, carro).
 */
export function useMapboxInstance({
  accessToken,
  canRenderMap,
  interactive,
  nativeControls,
  containerRef,
  initialCoordinates,
  initialZoom,
  setInitializationFailed,
}: {
  readonly accessToken?: string;
  readonly canRenderMap: boolean;
  /** `false` no mapa contextual (o pano de fundo desfocado do hotel). */
  readonly interactive: boolean;
  /** Os `+`/`−` do próprio Mapbox — só quando o produto não desenha os seus. */
  readonly nativeControls: boolean;
  readonly containerRef: { readonly current: HTMLDivElement | null };
  /** Where the camera is born. Read when the instance is created: a new value
   *  never rebuilds the map. */
  readonly initialCoordinates: MapCoordinates | undefined;
  readonly initialZoom: number;
  readonly setInitializationFailed: (failed: boolean) => void;
}): { readonly mapRef: LiveMapRef; readonly instance: MapboxInstance | null } {
  const mapRef = useRef<mapboxgl.Map>(null);
  // `mapRef` é ref: trocar `.current` não acorda efeito nenhum. Quem depende de
  // "há um mapa NOVO aqui" — o enquadramento — depende de `instance`, ou a
  // instância recriada (token ou controles trocados) nasce na câmera fixa e
  // fica lá enquanto as coordenadas não mudarem. Effects reach it through
  // `liveMap`, which drops a removed instance.
  const [instance, setInstance] = useState<MapboxInstance | null>(null);
  const initialView = useEffectEvent(() => ({ center: initialCoordinates, zoom: initialZoom }));
  useEffect(() => {
    const container = containerRef.current;
    if (!canRenderMap || !container) return undefined;
    const { center: initial, zoom } = initialView();
    if (!initial || !accessToken) return undefined;
    let created: mapboxgl.Map | undefined;
    let disposed = false;
    const create = (mb: Mapbox): void => {
      try {
        mb.accessToken = accessToken;
        const mapInstance = new mb.Map({
          accessToken,
          container,
          center: [initial.longitude, initial.latitude],
          style: 'mapbox://styles/mapbox/streets-v12',
          zoom,
          interactive,
        });
        created = mapInstance;
        if (interactive && nativeControls) {
          mapInstance.addControl(new mb.NavigationControl({ showCompass: false }), 'top-right');
        }
        // O `resize()` no `load` é a receita da própria Mapbox para marcador em
        // posição errada (mapbox-gl-js#10749): o mapa mede o contêiner na
        // construção, e aqui ele nasce dentro de um card que ainda está se
        // acomodando. Com a medida velha toda projeção sai deslocada, e o
        // sintoma aparece só ao dar zoom — o pino "anda" para fora da caixa,
        // porque é aí que a projeção é refeita.
        mapInstance.on('load', () => {
          mapInstance.resize();
        });
        mapRef.current = mapInstance;
        setInstance({ map: mapInstance });
      } catch {
        created?.remove();
        created = undefined;
        setInitializationFailed(true);
      }
    };
    // O `mapbox-gl` vem sob demanda (`loadMapbox`). Já carregado (o segundo mapa da
    // página), o mapa nasce neste mesmo efeito; senão, quando o módulo chega, se o
    // efeito ainda estiver de pé.
    const ready = loadedMapbox();
    if (ready) {
      create(ready);
    } else {
      loadMapbox()
        .then((mb) => {
          if (!disposed) create(mb);
        })
        .catch(() => {
          if (!disposed) setInitializationFailed(true);
        });
    }

    return () => {
      disposed = true;
      // Quem já desmontou o mapa (um marcador que falhou) soltou o `mapRef`: não remove de novo.
      if (!created || mapRef.current !== created) return;
      mapRef.current = null;
      created.remove();
    };
  }, [
    accessToken,
    canRenderMap,
    containerRef,
    interactive,
    nativeControls,
    setInitializationFailed,
  ]);
  return { mapRef, instance };
}

/** O contêiner mudou de tamanho (a divisória da página, a coluna que abre):
 *  o canvas é redimensionado por quadro e `onSettle` só é chamado quando o gesto
 *  assenta — é ele que reenquadra quem o consome. Com o `mapbox-gl` sob demanda
 *  (#5796) o mapa nasce depois do primeiro render: `instance` (de
 *  `useMapboxInstance`) liga o observador quando ele chega. */
export function useMapboxContainerResize({
  canRenderMap,
  containerRef,
  mapRef,
  instance,
  onSettle,
}: {
  readonly canRenderMap: boolean;
  readonly containerRef: { readonly current: HTMLDivElement | null };
  readonly mapRef: LiveMapRef;
  readonly instance: MapboxInstance | null;
  readonly onSettle?: () => void;
}): void {
  const settled = useEffectEvent(() => {
    onSettle?.();
  });
  useEffect(() => {
    const container = containerRef.current;
    const mapInstance = liveMap(mapRef, instance);
    if (!canRenderMap || !container || !mapInstance || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    let settle: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver(() => {
      mapRef.current?.resize();
      if (settle !== undefined) clearTimeout(settle);
      settle = setTimeout(() => {
        settle = undefined;
        settled();
      }, MAP_RESIZE_SETTLE_MS);
    });
    observer.observe(container);
    return () => {
      if (settle !== undefined) clearTimeout(settle);
      observer.disconnect();
    };
  }, [canRenderMap, containerRef, instance, mapRef]);
}

function anchorPart(className: string): HTMLSpanElement {
  const part = document.createElement('span');
  part.className = className;
  return part;
}

/**
 * A ÂNCORA do mapa: o lugar buscado (o compromisso do hotel, o aeroporto do
 * carro) — pílula com o glifo de lugar e o nome, haste e ponto na coordenada.
 * Em HTML cru porque vai num `Marker` do Mapbox, fora do React. Régua, não
 * alvo: `pointer-events: none` no CSS.
 */
export function mapAnchor(label: string): HTMLDivElement {
  const anchor = document.createElement('div');
  anchor.className = 'iuk-results-map-anchor';
  anchor.setAttribute('role', 'img');
  anchor.setAttribute('aria-label', label);

  // O glifo de LUGAR, e ele é peça e não caractere: o `◉` que morava aqui vinha
  // da fonte, então saía com o peso e o alinhamento que a fonte quisesse dar —
  // ao lado de um escudo e de uma etiqueta desenhados em máscara, era o único
  // símbolo do mapa fora da mesma régua. O nome acessível vive no `aria-label`
  // do conjunto.
  const anchorLabel = document.createElement('span');
  anchorLabel.className = 'iuk-results-map-anchor-label';
  anchorLabel.append(anchorPart('iuk-results-map-anchor-glyph'));
  const anchorName = anchorPart('iuk-results-map-anchor-name');
  anchorName.textContent = label;
  anchorLabel.append(anchorName);
  anchor.append(anchorLabel);

  const stem = document.createElement('span');
  stem.className = 'iuk-results-map-anchor-stem';
  anchor.append(stem);

  const point = document.createElement('span');
  point.className = 'iuk-results-map-anchor-point';
  anchor.append(point);
  return anchor;
}

/** Os controles de vista, numa coluna só (expandir · + · − · localizar): o
 *  expandir é controle de VISTA como os outros três, e solto no canto de cima
 *  ele caía sobre a lupa da barra de busca no celular, onde a pílula ocupa a
 *  largura toda. */
export function MapViewControls({
  controls,
  onZoomIn,
  onZoomOut,
  onRecenter,
}: {
  readonly controls: MapViewControlsCopy;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly onRecenter: () => void;
}): ReactNode {
  return (
    <div className="iuk-results-map-view-controls">
      {controls.onExpand && controls.expandLabel && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="shadow-md"
          aria-label={controls.expandLabel}
          onClick={controls.onExpand}
        >
          <ArrowsOutSimpleIcon aria-hidden="true" />
        </Button>
      )}
      <div className="iuk-results-map-zoom-controls">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="rounded-b-none shadow-md"
          aria-label={controls.zoomInLabel}
          onClick={onZoomIn}
        >
          <PlusIcon aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="-mt-px rounded-t-none shadow-md"
          aria-label={controls.zoomOutLabel}
          onClick={onZoomOut}
        >
          <MinusIcon aria-hidden="true" />
        </Button>
      </div>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="shadow-md"
        aria-label={controls.recenterLabel}
        onClick={onRecenter}
      >
        <CrosshairIcon aria-hidden="true" />
      </Button>
    </div>
  );
}
